import { useEffect } from "react";

interface BlogPostingSchemaProps {
  title: string;
  description: string;
  author: string;
  publishedAt: Date;
  modifiedAt?: Date;
  featuredImage: string;
  url: string;
  category?: string;
  wordCount?: number;
  content?: string;
}

export function BlogPostingSchema({
  title,
  description,
  author,
  publishedAt,
  modifiedAt,
  featuredImage,
  url,
  category,
  wordCount,
  content,
}: BlogPostingSchemaProps) {
  useEffect(() => {
    // Calculate word count from content if not provided
    const estimatedWordCount = wordCount || (content
      ? content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length
      : undefined);

    const schema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": title,
      "description": description,
      "image": {
        "@type": "ImageObject",
        "url": featuredImage,
      },
      "url": url,
      "author": {
        "@type": "Organization",
        "name": author,
        "url": "https://danispainting.net",
        "logo": {
          "@type": "ImageObject",
          "url": "https://danispainting.net/logo.png",
        },
      },
      "publisher": {
        "@type": "Organization",
        "name": "Danis Painting Co.",
        "url": "https://danispainting.net",
        "logo": {
          "@type": "ImageObject",
          "url": "https://danispainting.net/logo.png",
        },
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "133 E De La Guerra St, #267",
          "addressLocality": "Santa Barbara",
          "addressRegion": "CA",
          "postalCode": "93101",
          "addressCountry": "US",
        },
        "telephone": "+1-805-403-8727",
        "email": "danispainting@gmail.com",
      },
      "datePublished": publishedAt.toISOString(),
      "dateModified": modifiedAt ? modifiedAt.toISOString() : publishedAt.toISOString(),
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": url,
      },
      "isPartOf": {
        "@type": "Blog",
        "name": "Danis Painting Co. Blog",
        "url": "https://danispainting.net/blog",
      },
      "inLanguage": "en-US",
    };

    // Add optional fields
    if (category) {
      schema["articleSection"] = category;
    }
    if (estimatedWordCount) {
      schema["wordCount"] = estimatedWordCount;
    }

    // Create or update the schema script tag
    let scriptTag = document.querySelector('script[data-schema="blog-posting"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-schema', 'blog-posting');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(schema);

    return () => {
      // Clean up on unmount
      const tag = document.querySelector('script[data-schema="blog-posting"]');
      if (tag) {
        tag.remove();
      }
    };
  }, [title, description, author, publishedAt, modifiedAt, featuredImage, url, category, wordCount, content]);

  return null;
}
